import React, { useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { firebaseHelper } from "ui";
import { PrimaryButton } from "../studioxr/Button.tsx";
import { trackEvent } from "../libs/analyticsManager.ts";
import { sendExceptionToSentry } from "../libs/sentry";

export const CANCELLATION_FEEDBACK_COLLECTION = "cancellationFeedback";

// Mirrors Stripe's standard cancellation taxonomy so this data stays
// comparable with anything the Stripe portal survey collects later.
// Order is deliberate: "too expensive" is the socially safest answer and gets
// primacy-inflated at the top of a list, so it sits mid-list; "Other" is last.
export const CANCELLATION_REASONS: { value: string; label: string }[] = [
  { value: "unused", label: "I don't use it enough" },
  { value: "missing_features", label: "It's missing features I need" },
  { value: "too_complex", label: "It's too difficult to use" },
  { value: "too_expensive", label: "It's too expensive" },
  { value: "low_quality", label: "Quality didn't meet my expectations" },
  { value: "switched_service", label: "I'm switching to another product" },
  { value: "customer_service", label: "Customer support didn't meet my needs" },
  { value: "other", label: "Other" },
];

const MAX_COMMENT_LENGTH = 1000;

/**
 * Drop control characters (tab and newline survive) and clamp the length.
 * Pasted free text can carry them, and they corrupt the admin CSV/XLSX export.
 */
export function sanitizeComment(raw: string): string {
  const stripped = Array.from(raw)
    .filter((ch) => {
      const code = ch.charCodeAt(0);
      if (code === 9 || code === 10) return true;
      return code > 31 && code !== 127;
    })
    .join("");
  return stripped.trim().slice(0, MAX_COMMENT_LENGTH);
}

interface CancellationSurveyProps {
  uid?: string | null;
  email?: string | null;
  stripeCustomerId?: string | null;
}

/**
 * Optional churn survey shown AFTER a cancellation has already succeeded.
 * Deliberately outside the billing path: nothing here can block, delay or fail
 * a cancellation, and a failed write is swallowed (reported to Sentry) rather
 * than surfaced — losing one feedback doc is acceptable, adding friction for a
 * user who just cancelled is not.
 */
export const CancellationSurvey = ({
  uid,
  email,
  stripeCustomerId,
}: CancellationSurveyProps) => {
  const [feedback, setFeedback] = useState("");
  // Plain local draft — never persisted or pushed to a store on change.
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submitFeedback = async () => {
    if (!feedback || submitting || submitted) return;
    setSubmitting(true);
    const cleanComment = sanitizeComment(comment);

    try {
      trackEvent("Cancellation Feedback Submitted", {
        feedback,
        hasComment: cleanComment.length > 0,
      });
    } catch (error) {
      console.error("trackEvent Cancellation Feedback Submitted", error);
    }

    try {
      await addDoc(
        collection(firebaseHelper.db, CANCELLATION_FEEDBACK_COLLECTION),
        {
          uid: uid ?? null,
          email: email ?? null,
          stripeCustomerId: stripeCustomerId ?? null,
          feedback,
          comment: cleanComment,
          createdAt: serverTimestamp(),
        },
      );
    } catch (error) {
      sendExceptionToSentry({
        error,
        contextName: "cancellationFeedback",
        context: { uid, feedback },
      });
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <Typography sx={{ paddingTop: "8px", paddingBottom: "4px" }}>
        Thanks — this really helps.
      </Typography>
    );
  }

  return (
    <Box sx={{ paddingTop: "8px" }}>
      <Typography sx={{ fontWeight: 600 }}>What made you cancel?</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", pb: 1 }}>
        Optional — helps us improve Mattoboard.
      </Typography>

      <FormControl component="fieldset" fullWidth>
        <RadioGroup
          aria-label="What made you cancel?"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
        >
          {CANCELLATION_REASONS.map((reason) => (
            <FormControlLabel
              key={reason.value}
              value={reason.value}
              control={<Radio size="small" />}
              label={reason.label}
              componentsProps={{ typography: { sx: { fontSize: "14px" } } }}
              sx={{ marginY: "-4px" }}
            />
          ))}
        </RadioGroup>
      </FormControl>

      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        label="Anything else you'd like us to know?"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        inputProps={{ maxLength: MAX_COMMENT_LENGTH }}
        sx={{ mt: 1 }}
      />

      <PrimaryButton
        fullWidth
        variant="outlined"
        onClick={submitFeedback}
        disabled={!feedback || submitting}
        sx={{
          mt: 1.5,
          padding: "8px",
          fontWeight: 400,
          fontSize: "16px",
          textTransform: "capitalize",
          borderRadius: "8px",
          borderColor: "#000",
          color: "#000",
          "&:hover": { borderColor: "#000", background: "#f2f2f2" },
        }}
      >
        {submitting ? "Sending..." : "Send feedback"}
      </PrimaryButton>
    </Box>
  );
};
